import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { ExerciseBlock } from '@/types/workout';
import { ExerciseRow, normalizeExercisesToRows } from './exerciseRowSelector';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ExerciseSettings } from './ExerciseSettings';

interface CompactWorkoutTableProps {
  exercises: ExerciseBlock[];
  isPreset?: boolean;
  onExerciseUpdate?: (exerciseId: string, updates: Partial<ExerciseBlock>) => void;
  workoutId?: string;
}

/**
 * Formats exercise information in simplified format
 * Time-based: "1 set · 30s"
 * Reps-based: "3 sets · 10 reps @ 5kg" (if weight is set)
 */
function formatExerciseInfo(row: ExerciseRow, weight?: number): string {
  if (row.sets === '-') {
    // Time-based exercise
    return `1 set · ${row.repsOrTime}`;
  } else {
    // Reps-based exercise
    const setsText = row.sets === '1' ? 'set' : 'sets';
    const weightText = weight ? ` @ ${weight}kg` : '';
    return `${row.sets} ${setsText} · ${row.repsOrTime}${weightText}`;
  }
}

export function CompactWorkoutTable({ exercises, isPreset = false, onExerciseUpdate, workoutId }: CompactWorkoutTableProps) {
  const [selectedExerciseId, setSelectedExerciseId] = useState<string | null>(null);
  const rows = normalizeExercisesToRows(exercises);

  // Create a map for quick lookup of full exercise data
  const exerciseMap = new Map<string, ExerciseBlock>();
  exercises.forEach((exercise) => {
    exerciseMap.set(exercise.id, exercise);
  });

  // Maintain order: warmup → main → cooldown (matching Detailed view)
  // Use same logic as WorkoutOverview: warmup = phase === 'warmup', main = !phase || phase === 'main', cooldown = phase === 'cooldown'
  const orderedRows = [
    ...rows.filter((r) => r.phase === 'warmup'),
    ...rows.filter((r) => !r.phase || r.phase === 'main'),
    ...rows.filter((r) => r.phase === 'cooldown'),
  ];

  const selectedExercise = selectedExerciseId ? exerciseMap.get(selectedExerciseId) : null;

  // Handle exercise update (for non-preset workouts)
  const handleExerciseUpdate = (updates: Partial<ExerciseBlock>) => {
    if (!selectedExercise || !onExerciseUpdate) return;
    onExerciseUpdate(selectedExercise.id, updates);
    // Keep dialog open for further edits
  };

  return (
    <div>
      <div>
        {orderedRows.map((row, index) => {
          // Determine if this exercise should have margin (not in same superset as previous)
          const previousRow = index > 0 ? orderedRows[index - 1] : null;
          const nextRow = index < orderedRows.length - 1 ? orderedRows[index + 1] : null;
          
          const isInSameSupersetAsPrevious =
            previousRow &&
            row.supersetGroupId &&
            row.supersetGroupId === previousRow.supersetGroupId;
          
          const isInSameSupersetAsNext =
            nextRow &&
            row.supersetGroupId &&
            row.supersetGroupId === nextRow.supersetGroupId;
          
          // Only add margin if NOT in same superset as previous row
          // Cards should only touch when they belong to the same superset
          // Exception: last exercise in a superset should have mb-2
          let marginClass = '';
          if (isInSameSupersetAsNext) {
            // Part of superset - only last exercise in superset gets spacing
            marginClass = 'mb-0';
          } else {
            // Standalone
            marginClass = 'mb-2';
          }
          
          // Show indicator bar if this exercise is part of a superset
          const hasSuperset = !!row.supersetGroupId;

          // Determine border radius classes
          // First card in a group (superset or standalone): rounded top only
          // Last card in a group: rounded bottom only
          // Middle cards in superset: no rounding at all
          // Standalone cards: all rounded
          let borderRadiusClasses = '';
          if (isInSameSupersetAsPrevious && isInSameSupersetAsNext) {
            // Middle card in superset - no rounding anywhere
            borderRadiusClasses = 'rounded-none';
          } else if (isInSameSupersetAsPrevious && !isInSameSupersetAsNext) {
            // Last card in superset - only bottom corners rounded
            borderRadiusClasses = 'rounded-b-lg';
          } else if (!isInSameSupersetAsPrevious && isInSameSupersetAsNext) {
            // First card in superset - only top corners rounded
            borderRadiusClasses = 'rounded-t-lg';
          } else {
            // Standalone card - all rounded
            borderRadiusClasses = 'rounded-lg';
          }

          // Determine bar styling - bar should span full height of superset group
          let barClasses = 'absolute left-0 top-0 bottom-0 w-1 bg-neutral-900';
          if (hasSuperset) {
            if (!isInSameSupersetAsPrevious && !isInSameSupersetAsNext) {
              // Standalone superset card - rounded on both ends
              barClasses += ' rounded-l-lg';
            } else if (!isInSameSupersetAsPrevious) {
              // First card in superset - rounded top left
              barClasses += ' rounded-tl-lg';
            } else if (!isInSameSupersetAsNext) {
              // Last card in superset - rounded bottom left
              barClasses += ' rounded-bl-lg';
            }
            // Middle cards have no rounding - bar connects seamlessly
          }

          return (
            <div
              key={row.id}
              className={`border border-neutral-200 bg-white px-4 py-3 relative ${marginClass} ${
                isInSameSupersetAsPrevious ? 'border-t-0' : ''
              } ${borderRadiusClasses} hover:bg-neutral-50 transition-colors ${
                !isPreset ? 'cursor-pointer' : ''
              }`}
              onClick={() => !isPreset && setSelectedExerciseId(row.id)}
            >
              {/* Black vertical bar indicator for supersets - spans full height of card */}
              {hasSuperset && (
                <div className={barClasses} />
              )}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-neutral-900 mb-0.5">{row.name}</h4>
                  <p className="text-sm text-neutral-600">
                    {formatExerciseInfo(row, exerciseMap.get(row.id)?.target.weight)}
                  </p>
                </div>
                {!isPreset && (
                  <ChevronRight className="w-5 h-5 text-neutral-400 flex-shrink-0 ml-3" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Exercise Details Dialog */}
      {selectedExercise && (
        <Dialog open={!!selectedExercise} onOpenChange={(open) => !open && setSelectedExerciseId(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{selectedExercise.name}</DialogTitle>
              <DialogDescription>{selectedExercise.instruction}</DialogDescription>
            </DialogHeader>
            <ExerciseSettings exercise={selectedExercise} onUpdate={handleExerciseUpdate} workoutId={workoutId} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

